/**
 * @ComponentFor ArticlePageViewModel
 */

import React, { Ref, useRef, useEffect } from 'react';
import {scrollPosition} from '@polarnopyret/scope';
import connect from 'Shared/connect';
import { isCompact as functionIsCompact } from 'Shared/Viewport';
import { styled } from '@glitz/react';
import ArticlePageViewModel from './ArticlePageViewModel.type';
import {
  H1,
  ResponsiveSize,
  ResponsiveLineHeight,
  Paragraph,
  ArticleTag,
  TextBlockSmall,
  HeadingLabel,
} from 'Shared/SharedComponents/atoms/Typography';
import { EpiProperty, epiPropertyValue } from '@polarnopyret/scope-episerver';
import { AspectImageVideoBackground } from 'Shared/SharedComponents/molecules/ImageVideoBackground';
import { Down24x40, Up32x64 } from 'Shared/Icons/ArrowSwirl';
import { Preset } from 'Shared/image-sizes';
import { calculateTopPadding, Ratio } from 'Shared/AspectRatio';
import Page from 'Shared/Page';
import { BreadcrumbWrapper } from 'Shared/Page/PageContent';
import { createGridClasses } from 'Shared/grid';
import { selectDeviceImageFromProperty } from 'Shared/utils';
import * as style from 'Shared/Style';

const FlexElement = styled.div({
  display: 'flex',
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'top',
  color: style.colors.monochrome.white,
  paddingTop: '43%',
  ['@media ' + style.mediaMinLarge]: {
    paddingTop: '33%',
  },
});

const FlexColumnElement = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  color: style.colors.monochrome.white,
  cursor: 'pointer',
});

const UpSwirl = styled(Up32x64, {
  position: 'fixed', 
  bottom: '24px', 
  left: '48px', 
  cursor: 'pointer'
})

const Breadcrumbs = styled(BreadcrumbWrapper, {
  color: style.colors.monochrome.black,
})

const Label = styled(HeadingLabel, {
  marginBottom: '16px'
})

const HeadingWrap = styled.div({
  marginBottom: '16px'
})

const TagContainer = styled.div({
  display: 'flex', 
  flexDirection: 'column', 
  justifyContent: 'end', 
  marginBottom: '16px' 
})

const IntroWrap = styled.div({
  margin: { y: '40px' },
  ['@media ' + style.mediaMinLarge]: {
    margin: { y: '80px' },
  },
})

const TagLabel = styled(TextBlockSmall, {
  marginBottom: '8px'
})

export const ArticlePage = connect(
  (state) => ({
    currentBreakpoint: state.currentBreakpoint,
  }),
  (dispatch) => ({}),
)((props: ArticlePageViewModel & { currentBreakpoint: number }) => {
  const { currentBreakpoint, breadcrumbs, page, labels } = props;
  const { heading, tag, tags, image, imageSmallScreen, mainIntro } = page;
  const preambleRef: Ref<HTMLDivElement> = useRef();
  const isCompact = functionIsCompact(currentBreakpoint);
  const introText = epiPropertyValue(mainIntro);

  useEffect(() => {
    window?.document && scrollPosition(0);
  }, []);

  const imageContainerPadding = isCompact ? `${calculateTopPadding(Ratio.ThreeToFour)}` : `${calculateTopPadding(Ratio.SixteenToNine)}`;

  return (
    <Page>
      {!isCompact && (
        <UpSwirl
          onClick={() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth', });
          }}
        />
      )}
      <Breadcrumbs isCompact={isCompact} breadcrumbs={breadcrumbs}>
        <div className={createGridClasses(12, 11, isCompact ? '' : 'offset-lg-2 grid subgrid-11')}>
          <Label fontSize={ResponsiveSize.D16_M16} upperCase className={createGridClasses(12, 7)}>
            {tag && epiPropertyValue(tag)}
          </Label>
          <HeadingWrap className={createGridClasses(12, 7)}>
            <H1 upperCase={false} fontSize={ResponsiveSize.D56_M32}>
              {heading && epiPropertyValue(heading)}
            </H1>
          </HeadingWrap>
          {epiPropertyValue(tags)?.length > 0 && (
            <TagContainer className={createGridClasses(12, 3, 'offset-lg-9')}>
              <TagLabel upperCase={false}>
                {labels.tags}
              </TagLabel>
              <ArticleTag>{epiPropertyValue(tags)?.join(', ')}</ArticleTag>
            </TagContainer>
          )}
          <div className={createGridClasses(12, 11)}>
            <AspectImageVideoBackground
              paddingTop={ imageContainerPadding }
              image={selectDeviceImageFromProperty(image, imageSmallScreen, isCompact)}
              breakPoint={currentBreakpoint}
              preset={Preset.FullWidth}
              borderRadius={8}
            >
              <FlexElement>
                <FlexColumnElement onClick={() => preambleRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
                  <Paragraph fontSize={ResponsiveSize.D16_M16} lineHeight={ResponsiveLineHeight.D130_M130}>
                    {labels && labels.scrollToArticle}
                  </Paragraph>
                  <Down24x40 />
                </FlexColumnElement>
              </FlexElement>
            </AspectImageVideoBackground>
          </div>
          {introText && (
            <IntroWrap className={createGridClasses(12, 8)} ref={preambleRef}>
              <Paragraph fontSize={ResponsiveSize.D32_M24}>{introText}</Paragraph>
            </IntroWrap>
          )}
        </div>
      </Breadcrumbs>
      <EpiProperty for={page.mainContentArea} />
    </Page>
  );
});

export default ArticlePage;
